import { useSelector } from 'react-redux';
import styled from 'styled-components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useLocalization } from '../../../../../../../../../../../lib/hooks/useLocalization';
import { DroppableFieldWrapper } from '../../DroppableItems/DroppableFieldWrapper';
import { DroppableImageWrapper } from '../../DroppableItems/DroppableImageWrapper';
import { AppCheckbox } from '../../../../../../../../../../../ui/AppCheckbox';
import { AppText } from '../../../../../../../../../../../ui/text/AppText';
import { QuestionnaireFormSection } from '../../../../../features/QuestionnaireFormSection';
import { hasRichTextContent } from '../../../../../../../../../../../lib/util/rich-text';

/**
 * @desc The intro is the starting page for the finder. The intro should be at the beginning of Finder, and other elements cannot be added to this page.
 * @param {Object} item
 * @param {Function} handleChangeFieldValue
 * @param {Function} handleChangeContentFields
 * @param {Function} onChangeFieldInfoIconValue
 * @param {Function} onDeleteInfoIcon
 * @returns {JSX.Element}
 */

// eslint-disable-next-line max-lines-per-function
export function IntroForm({ item, handleChangeFieldValue, handleChangeContentFields, onChangeFieldInfoIconValue, onDeleteInfoIcon }) {
  const { content = {}, id } = item;
  const { headline, subline, primaryButton, secondaryButton, selectBackgroundImage, customerCode, privacyNotice } = content;
  const {
    templateHeadline,
    templateSubline,
    templatePrimaryButton,
    templateSecondaryButton,
    selectImage,
    templateInfo,
    customerCodeHeadline,
    customerCodeEnabled,
    introPrivacyNoticeContent,
    introPrivacyCheckboxLabel,
    introPrivacyCheckboxEnabled,
  } = useLocalization().translations.unique.formItems;

  const {
    isViewMode,
    finder: {
      finderSettings: { media },
    },
  } = useSelector(({ finders }) => finders);

  const handleChangeCustomerCode = (fieldName, value) => {
    handleChangeContentFields({
      customerCode: {
        ...customerCode,
        [fieldName]: value,
      },
    });
  };

  const handleChangePrivacyNotice = (fieldName, value) => {
    handleChangeContentFields({
      privacyNotice: {
        ...privacyNotice,
        [fieldName]: value,
      },
    });
  };

  // the privacy notice is authored as rich text, so an "empty" editor still reports markup like `<p><br></p>`
  const handleChangePrivacyNoticeContent = value => {
    handleChangePrivacyNotice('content', hasRichTextContent(value) ? value : '');
  };

  const handleTogglePrivacyCheckbox = event => {
    handleChangePrivacyNotice('checkbox', {
      ...privacyNotice?.checkbox,
      enabled: event.target.checked,
    });
  };

  return (
    <Root>
      <DroppableFieldWrapper
        itemId={id}
        labelField={templateHeadline.label}
        labelTemplateInfo={templateInfo.label}
        type={!item.content.headline.infoIcon && 'INFO_ICON'}
        valueObject={headline}
        onChangeField={handleChangeFieldValue}
        onChangeFieldInfoIconValue={onChangeFieldInfoIconValue}
        onDeleteInfoIcon={onDeleteInfoIcon}
        fieldName='headline'
        infoIcon={item.content.headline.infoIcon}
      />

      <ToggleRow>
        <AppCheckbox
          checked={customerCode?.enabled}
          onChange={event => handleChangeCustomerCode('enabled', event.target.checked)}
          textValue={customerCodeEnabled.label}
        />
      </ToggleRow>
      <QuestionnaireFormSection
        textFieldProps={{
          label: customerCodeHeadline.label,
          value: customerCode?.headline?.value,
          onChange: event =>
            handleChangeCustomerCode('headline', {
              ...customerCode?.headline,
              value: event.target.value,
            }),
        }}
      />

      <ToggleRow>
        <AppCheckbox
          checked={privacyNotice?.checkbox?.enabled}
          onChange={handleTogglePrivacyCheckbox}
          textValue={introPrivacyCheckboxEnabled.label}
        />
      </ToggleRow>
      <PrivacyNoticeEditor>
        <PrivacyNoticeLabel>
          <AppText textValue={introPrivacyNoticeContent.label} />
        </PrivacyNoticeLabel>
        <ReactQuill theme='snow' readOnly={isViewMode} value={privacyNotice?.content || ''} onChange={handleChangePrivacyNoticeContent} />
      </PrivacyNoticeEditor>
      <QuestionnaireFormSection
        textFieldProps={{
          label: introPrivacyCheckboxLabel.label,
          value: privacyNotice?.checkbox?.label,
          onChange: event =>
            handleChangePrivacyNotice('checkbox', {
              ...privacyNotice?.checkbox,
              label: event.target.value,
            }),
        }}
      />

      <DroppableFieldWrapper
        itemId={id}
        labelField={templateSubline.label}
        labelTemplateInfo={templateInfo.label}
        type={!item.content.subline.infoIcon && 'INFO_ICON'}
        valueObject={subline}
        onChangeField={handleChangeFieldValue}
        onChangeFieldInfoIconValue={onChangeFieldInfoIconValue}
        onDeleteInfoIcon={onDeleteInfoIcon}
        fieldName='subline'
        infoIcon={item.content.subline.infoIcon}
      />
      <DroppableFieldWrapper
        itemId={id}
        labelField={templatePrimaryButton.label}
        labelTemplateInfo={templateInfo.label}
        type={!item.content.primaryButton.infoIcon && 'INFO_ICON'}
        valueObject={primaryButton}
        onChangeField={handleChangeFieldValue}
        onChangeFieldInfoIconValue={onChangeFieldInfoIconValue}
        onDeleteInfoIcon={onDeleteInfoIcon}
        fieldName='primaryButton'
        infoIcon={item.content.primaryButton.infoIcon}
      />
      <DroppableFieldWrapper
        itemId={id}
        labelField={templateSecondaryButton.label}
        labelTemplateInfo={templateInfo.label}
        type={!item.content.secondaryButton.infoIcon && 'INFO_ICON'}
        valueObject={secondaryButton}
        onChangeField={handleChangeFieldValue}
        onChangeFieldInfoIconValue={onChangeFieldInfoIconValue}
        onDeleteInfoIcon={onDeleteInfoIcon}
        fieldName='secondaryButton'
        infoIcon={item.content.secondaryButton.infoIcon}
      />
      <DroppableImageWrapper
        itemId={id}
        labelField={selectImage.label}
        labelTemplateInfo={templateInfo.label}
        type={!item.content.selectBackgroundImage.infoIcon && 'INFO_ICON'}
        valueObject={selectBackgroundImage}
        onChangeField={handleChangeFieldValue}
        onChangeFieldInfoIconValue={onChangeFieldInfoIconValue}
        onDeleteInfoIcon={onDeleteInfoIcon}
        fieldName='selectBackgroundImage'
        infoIcon={item.content.selectBackgroundImage.infoIcon}
        media={media}
      />
    </Root>
  );
}

const Root = styled('div')({
  padding: '20px 25px',
});

const ToggleRow = styled('div')({
  margin: '20px 0 12px',
});

// quill sizes `.ql-container` with `height: 100%`, so the editor needs an explicit height to stop the next
// form section, whose label is absolutely positioned above its input, from overlapping it
const PrivacyNoticeEditor = styled('div')({
  marginBottom: '32px',

  '.ql-container': {
    height: 'auto',
  },

  '.ql-editor': {
    minHeight: '120px',
  },
});

const PrivacyNoticeLabel = styled('div')({
  marginBottom: '8px',
});
